import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { combineLatest, Subscription } from 'rxjs';

import { AppState } from 'src/app/state';
import {
  addContentItemToIntervention,
  removeContentItemFromIntervention,
  saveContentItemsSortingOrder,
} from '../../../../state/intervention-content-item/intervention-content-item.actions';
import { selectModules } from '../../../../state/module/module.selectors';
import { selectSelectedIntervention } from 'src/app/dashboard/state/intervention/intervention.selectors';
import {
  selectContentItemsSortingOrder,
  selectInterventionContentItems,
} from '../../../../state/intervention-content-item/intervention-content-item.selectors';
import { DASHBOARD_ROUTES } from '../../../../dashboard.routes';
import { Module } from '../../../../../models/module';
import { InterventionContentItem } from '../../../../../models/intervention-content-item';
import { Intervention } from 'src/app/models/intervention';
import { PrivilegeService } from '../../../../services/privilege/privilege.service';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../../core/dashboard-constants';
import { getModule, getModulesInSelectedIntervention } from '../../../../state/module/module.actions';
import {
  getInterventionContentItemSortOrderArray,
  reorderInterventionContentItems,
  SELECT_LIST_ITEM_TYPE,
} from '../../../helpers/intervention-helper';
import { TimingRulesDialogComponent } from '../../../components/timing-rules-dialog/timing-rules-dialog.component';
import { getCalculatedVariablesForStudy } from 'src/app/dashboard/state/calculated-variable/calculated-variable.actions';

@Component({
  selector: 'dashboard-intervention-modules',
  templateUrl: './intervention-modules.component.html',
  styleUrls: ['./intervention-modules.component.scss'],
})
export class InterventionModulesComponent implements OnInit {
  interventionModulesForm: FormGroup;

  modules: Module[];
  interventionContentItems: InterventionContentItem[];
  selectedIntervention: Intervention;
  selectModuleList = [];
  colors = [
    '#46AE9B', '#FFC107', '#FF5722', '#673AB7', '#2196F3', '#4CAF50', '#FF9800', '#9C27B0',
    '#03A9F4', '#E91E63', '#00BCD4', '#8BC34A', '#CDDC39', '#FFEB3B', '#795548', '#607D8B',
    '#F44336', '#3F51B5', '#00C853', '#D500F9', '#1B5E20', '#FF6F00', '#004D40', '#FF3D00',
    '#BF360C', '#1A237E', '#AEEA00', '#2962FF', '#304FFE', '#6200EA', '#AA00FF', '#C51162',
    '#C2185B', '#E040FB', '#6D4C41', '#388E3C', '#7B1FA2', '#0288D1', '#0277BD', '#01579B',
    '#B0BEC5', '#FFD600', '#F50057', '#009688', '#6A1B9A', '#FF9100', '#43A047', '#673AB7'
  ];

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private router: Router,
    public privilegeService: PrivilegeService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.interventionModulesForm = this.formBuilder.group({
      moduleId: [null],
    });

    this.subscriptions.add(
      this.store
        .select(selectSelectedIntervention)
        .subscribe((intervention) => (this.selectedIntervention = intervention))
    );

    this.subscriptions.add(
      combineLatest([
        this.store.select(selectModules),
        this.store.select(selectInterventionContentItems),
        this.store.select(selectContentItemsSortingOrder),
      ]).subscribe(([modules, interventionContentItems, contentItemsSortingOrder]) => {
        this.interventionContentItems = reorderInterventionContentItems(
          contentItemsSortingOrder,
          interventionContentItems
        );
        this.modules = modules.filter((module) => !module.enrolment);
        this.createSelectModuleList();
      })
    );
  }

  get moduleId(): AbstractControl {
    return this.interventionModulesForm.get('moduleId') as AbstractControl;
  }

  onAddModule(): void {
    if (!this.moduleId.value) {
      return;
    }

    this.store.dispatch(
      addContentItemToIntervention({
        studyId: this.selectedIntervention.studyId,
        interventionId: this.selectedIntervention.id,
        itemId: this.moduleId.value,
        itemType: SELECT_LIST_ITEM_TYPE.MODULE,
      })
    );
  }

  onDelete(contentItem: InterventionContentItem): void {
    this.store.dispatch(
      removeContentItemFromIntervention({
        studyId: this.selectedIntervention.studyId,
        interventionId: this.selectedIntervention.id,
        itemId: contentItem.module.id,
        itemType: SELECT_LIST_ITEM_TYPE.MODULE,
        interventionContentItemId: contentItem.id,
      })
    );
  }

  onOpenModulesTab(contentItem?: InterventionContentItem): void {
    if (
      this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.ALL) ||
      this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.VIEW)
    ) {
      if (contentItem) {
        this.store.dispatch(getModule({ studyId: contentItem?.module?.studyId, moduleId: contentItem?.module?.id }));
      }
      this.router.navigate([`${DASHBOARD_ROUTES.STUDIES}/${this.selectedIntervention.studyId}/modules`]);
    }
  }

  dropItem(event: CdkDragDrop<InterventionContentItem[]>): void {
    moveItemInArray(this.interventionContentItems, event.previousIndex, event.currentIndex);
    this.saveItemSortingOrder(event.container.data);
  }

  saveItemSortingOrder(itemsArray: InterventionContentItem[]): void {
    const sortingOrder = getInterventionContentItemSortOrderArray(itemsArray);
    this.store.dispatch(
      saveContentItemsSortingOrder({
        studyId: this.selectedIntervention.studyId,
        interventionId: this.selectedIntervention.id,
        sortingOrder,
      })
    );
  }

  createSelectModuleList(): void {
    this.selectModuleList = this.modules.filter(
      (module) =>
        !this.interventionContentItems.find((moduleToBeRemoved) => moduleToBeRemoved.module?.id === module?.id)
    );
  }

  openTimingRulesDialog(contentItem: InterventionContentItem): void {
    this.dialog.open(TimingRulesDialogComponent, {
      data: { contentItem , interventionContentItems: this.interventionContentItems},
      width: '100%',
      panelClass: 'dashboard-timing-rules-no-padding-dialog',
    });
    this.store.dispatch(
      getModule({
        studyId: this.selectedIntervention.studyId,
        moduleId: contentItem?.moduleId
    })
    );
    this.store.dispatch(
      getModulesInSelectedIntervention({
        studyId: this.selectedIntervention.studyId,
        interventionId: this.selectedIntervention.id,
      })
    );
    this.store.dispatch(
      getCalculatedVariablesForStudy({
        studyId: this.selectedIntervention.studyId
      })
    );
  }
}
